import { Box, MenuItem } from '@mui/material'
import _ from 'lodash'
import moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Card, Col, Row } from 'react-bootstrap'
import { useHistory } from 'react-router-dom'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/**API */
import ClinicApi, { BranchInterface } from 'api/master/clinic.api'
import SearchPatients from 'component/SearchPatients'
import ComponentApi from 'api/components-api'
import OperativeTypeApi, { OperativeTypeObjInterface } from 'api/setting/treatments/operative-type.api'
import ReportApi, { FindAllReportInterface } from 'api/report.api'
import { getBranch, toBuddhistYear } from 'utils/app.utils'

/**COMPONENT */
import HeaderCard from 'component/CardCustom/HeaderCard'
import { useDownloadExcel } from 'component/Excel/hooks/useExcel'
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker'
import Loading from 'component/Loading'
import FilterSelect from 'component/Select/FilterSelect'
import AppointmentList from './AppointmentList'

/**STYLE */
import ButtonCustom from 'component/Button/ButtonCustom'
import InputTextSearch from 'component/Input/InputTextSearch'
import ExcelCodeFilterSelect from 'component/Search/ExcelCodeFilterSelect'
import * as UseStyled from 'features/report/useStyled'

type Doctors = {
  userId: number
  firstname: string
  lastname: string
  fullname: string
}

export interface DentistInterface {
  dentistId: number
  dentistFullname: string
}

export default function Appointment() {
  const { t } = useTranslation()
  const history = useHistory()
  const componentRef = useRef<HTMLDivElement>(null)
  const momentNow = moment().format('YYYY-MM-DD')
  const momentNowRange = [moment().startOf('month').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]
  const momentDate = () => momentNowRange
  const [branchId, setBranchId] = useState<any>(getBranch())
  const [branches, setBranches] = useState<any[]>([])
  /** TDH customization — appointment channel filter (QT-TDH-2026-003 v3.1) */
  const [appointmentChannelId, setAppointmentChannelId] = useState<number>(0)
  const [appointmentChannels, setAppointmentChannels] = useState<Array<{ appointmentChannelId: number; channelName: string; channelNameEn?: string }>>([])
  /** TDH customization — status / patient / treatment filters */
  /** ใช้ 'ALL' เป็น sentinel แทนค่า empty — MUI Select ไม่ trigger renderValue เมื่อ value=''
   *  ทำให้ label หาย ใน FilterSelect (tester พบใน MD-2606-0291) */
  const [statusAppointment, setStatusAppointment] = useState<string>('ALL')
  /** patient filter: single-select like register page (SearchPatients) */
  const [selectedPatientId, setSelectedPatientId] = useState<number>(0)
  const [selectedPatientName, setSelectedPatientName] = useState<string>('')
  const [operativeTypes, setOperativeTypes] = useState<OperativeTypeObjInterface[]>([])
  const [selectedOperatives, setSelectedOperatives] = useState<number[]>([])
  const [rowCount, setRowCount] = useState<number>(0)
  const [sortBy, setSortBy] = useState<string>('createAt')
  const [sortType, setSortType] = useState<string>('ASC')
  const [doctors, setDoctors] = useState<Doctors[]>([])
  const [date, setDate] = useState<string>(momentNow)
  const [page, setPage] = useState<number>(1)
  const [search, setSearch] = useState<string>('')
  const [pageLimit, setPageLimit] = useState<number>(10)
  const [rangeDate, setRangeDate] = useState<string[]>(momentDate)
  const [fetch, setFetch] = useState<boolean>(false)

  const [loading, setLoading] = useState<boolean>(true)
  const [selectDoctors, setSelectDoctors] = useState<any[]>([])

  const [doctorsMultiple, setDoctorsMultiple] = useState<DentistInterface[]>([])

  const [appointments, setAppointments] = useState<[]>([])
  const [exportAppointments, setExportAppointments] = useState<[]>([])

  const getAllFilter = () => {
    return [
      {
        label: t('REPORT.FILTER.BRANCH'),
        value: _.get(_.find(branches, { branchId: branchId }), 'branchName' || 'branchNameEn')
      },
      {
        label: t('REPORT.FILTER.DOCTOR'),
        value: getFilterUser()
      },
      {
        label: t('REPORT.FILTER.DATE_START'),
        value: rangeDate[0] ? toBuddhistYear(moment(rangeDate[0]), 'DD/MM/YYYY') : '-'
      },
      {
        label: t('REPORT.FILTER.DATE_END'),
        value: rangeDate[1] ? toBuddhistYear(moment(rangeDate[1]), 'DD/MM/YYYY') : '-'
      },
      {
        label: t('REPORT.FILTER.APPOINTMENT_CHANNEL'),
        value: appointmentChannelId
          ? (_.find(appointmentChannels, { appointmentChannelId })?.channelName || '-')
          : t('REPORT.FILTER.ALL')
      },
      {
        label: t('REPORT.FILTER.APPOINTMENT_STATUS'),
        value: statusAppointment && statusAppointment !== 'ALL' ? t(`APPOINTMENT.STATUS.${statusAppointment}`) : t('REPORT.FILTER.ALL')
      },
      {
        label: t('REPORT.FILTER.PATIENT'),
        value: selectedPatientId ? (selectedPatientName || '-') : t('REPORT.FILTER.ALL')
      },
      {
        label: t('REPORT.FILTER.TYPE_OPERATION'),
        value: selectedOperatives.length
          ? operativeTypes.filter((o) => selectedOperatives.includes(o.operativeTypeId)).map((o) => o.operativeTypeName).join(', ')
          : t('REPORT.FILTER.ALL')
      }
    ]
  }

  const getFilterUser = () => {
    if (_.includes(selectDoctors, 'ALL')) return ` ${t('REPORT.REPORT_GROUP.ALL')}`
    return ` ${doctors
      .filter((d: Doctors) => selectDoctors.includes(d.userId))
      .map((d: Doctors) => d.fullname)
      .join(', ')}`
  }

  const verifyPermission = async () => {
    const condition: FindAllReportInterface = {
      page: 1,
      pageLimit: 200
    }
    // ReportApi.reportList(condition).then(({ data }) => {
    //   const check = _.find(data, (val: any) => String(val.reportKey) === 'DF_LAB') ? true : false
    //   if (!check) {
    //     history.push(routeName.report)
    //     return false
    //   }
    // })

    ClinicApi.findAllBranches()
      .then(({ data }) => setBranches(data))
      .catch((e) => {
        return
      })
    ClinicApi.findAllDoctors('', '', rangeDate[0], rangeDate[1])
      .then((resp) => {
        setDoctors([...resp.data])
        setSelectDoctors(['ALL', ...resp.data.map((d: any) => d.userId)])
      })
      .catch((e) => {
        return
      })

    // DoctorScheduleApi.findAllDoctor({ branchId: branchId }).then(({ data: dataAllDoctor }) => {
    //     const dentists: DentistInterface[] = dataAllDoctor.map((d: any) => {
    //         return {
    //             dentistId: d.userId,
    //             dentistFullname: d.fullname
    //         }
    //     })
    //     setDoctorsMultiple(dentists)
    //     setSelectDoctors(['ALL', ...dentists.map((d: DentistInterface) => d.dentistId)])
    // })

    return true
  }

  useEffect(() => {
    verifyPermission()
  }, [branchId])

  useEffect(() => {
    /** patient list is no longer pre-loaded — SearchPatients does server-side search on demand */
    ComponentApi.findAllAppointmentChannels().then((res: any) => {
      if (res?.status === 200) setAppointmentChannels(res.data || [])
    })
    OperativeTypeApi.findAllActive({ pageLimit: 9999 } as any).then((res: any) => {
      if (res?.status === 200) setOperativeTypes(res.data || [])
    })
  }, [])

  const fetchAppointment = useCallback(async () => {
    let condition: any = {
      page: page,
      pageLimit: pageLimit,
      branchId: branchId || null,
      dateStart: rangeDate[0],
      dateEnd: rangeDate[1]
    }
    if (!_.isEmpty(search)) {
      condition.search = search
    }
    if (doctors) {
      const doctor = _.filter(selectDoctors, (v: any) => v > 0).join(',')
      if (doctor) condition = { ...condition, doctorId: _.filter(selectDoctors, (v: any) => v > 0).join(',') }
    }
    if (appointmentChannelId) condition = { ...condition, appointmentChannelId }
    if (statusAppointment && statusAppointment !== 'ALL') condition = { ...condition, statusAppointment }
    if (selectedPatientId) condition = { ...condition, patientId: String(selectedPatientId) }
    if (selectedOperatives.length) condition = { ...condition, operativeTypeId: selectedOperatives.join(',') }
    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }

    ReportApi.reportAppointment(condition)
      .then(({ headers, data }) => {
        setRowCount(headers['x-total'])
        setAppointments(data.rows)
        setExportAppointments(data.rowsExport)
        setFetch(false)
      })
      .catch((e) => {
        setLoading(false)
        setFetch(false)
      })
      .finally(() => {
        setLoading(false)
        setFetch(false)
      })
  }, [page, pageLimit, sortBy, sortType, fetch])

  const { onDownload } = useDownloadExcel({
    tableExportRef: componentRef.current,
    filename: `${t(`REPORT.REPORT_NAME.APPOINTMENT`)} (${date})`,
    sheetName: `${t(`REPORT.REPORT_NAME.APPOINTMENT`)}`
  })

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangeRowsPerPage = (limit: number) => setPageLimit(limit)

  const handleChangePage = (val: number) => setPage(val)

  useEffect(() => {
    fetchAppointment()
  }, [fetchAppointment])

  return (
    <>
      <Card className={'border-0 h-100'}>
        <HeaderCard text={t(`REPORT.REPORT_NAME.APPOINTMENTS`)} />
        <Card.Body>
          <Card.Title className={'mb-0'}>
            <Row className="mx-0">
              <Col xs={12} sm={6} md={6} xl={2} className={'pt-2 px-1'}>
                <InputTextSearch
                  keyInput={'search'}
                  label={t('REPORT.REPORT_NAME.SEARCH_APPOINTMENT')}
                  value={search}
                  onchange={(event) => {
                    setSearch(event.target.value)
                  }}
                  onClear={(event) => {
                    setSearch('')
                  }}
                />
              </Col>
              <Col xs={12} sm={6} md={6} xl={2} className={'pt-2 px-1'}>
                <FilterSelect
                  onchange={(e) => {
                    setBranchId(e.target.value)
                    setPage(1)
                  }}
                  renderValue={() => (branchId === 0 ? `${t('REPORT.FILTER.BRANCH')}: ${t('REPORT.REPORT_GROUP.ALL')}` : `${t('REPORT.FILTER.BRANCH')}: ${_.get(_.find(branches, { branchId: branchId }), 'branchName' || 'branchNameEn')}`)}
                  label={''}
                  selectId="select-branch"
                  labelId="label-branch"
                  value={branchId}
                  options={[
                    <MenuItem key={0} value={0}>
                      {t(`REPORT.FILTER.ALL`)}
                    </MenuItem>,
                    ..._.map(branches, (b: BranchInterface, index) => (
                      <MenuItem key={b.branchId} value={b.branchId}>
                        {b.branchName || b.branchNameEn}
                      </MenuItem>
                    ))
                  ]}
                  formControlStyle={{ maxWidth: 'unset !important' }}
                  classesOption="style-select-doctors"
                />
              </Col>
              <Col xs={12} sm={6} md={6} xl={2} className={'pt-2 px-1'}>
                <InputSecondNewRangePicker
                  inputHeight={32}
                  value={rangeDate || emptyRangePicker}
                  onchange={(val: any) => {
                    if (_.isEmpty(val)) setRangeDate(emptyRangePicker)
                    else setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
                  }}
                  label={''}
                />
              </Col>
              <Col xs={12} sm={6} md={6} xl={2} className={'pt-2 px-1'}>
                <ExcelCodeFilterSelect
                  inputLabel={t('REPORT.FILTER.DOCTOR')}
                  placeholder={`${t('INPUT.SEARCH')} ${t('REPORT.FILTER.DOCTOR')}...`}
                  data={_.map(doctors, (d) => ({
                    id: d.userId,
                    value: d.fullname,
                    label: d.fullname
                  })) as any}
                  selectedRows={_.map(doctors, (d) => ({
                    id: d.userId,
                    value: d.fullname,
                    label: d.fullname
                  })).filter((r) => selectDoctors.includes(r.id)) as any}
                  onSelectionChange={(rows: any) => setSelectDoctors(rows.map((r: any) => r.id))}
                  maxTags={1}
                />
              </Col>
              <Col xs={12} sm={6} md={6} xl={2} className={'pt-2 px-1'}>
                <FilterSelect
                  onchange={(e) => {
                    setAppointmentChannelId(Number(e.target.value) || 0)
                    setPage(1)
                  }}
                  renderValue={() =>
                    `${t('REPORT.FILTER.APPOINTMENT_CHANNEL')}: ${
                      appointmentChannelId
                        ? _.find(appointmentChannels, { appointmentChannelId })?.channelName || '-'
                        : t('REPORT.FILTER.ALL')
                    }`
                  }
                  label={''}
                  selectId="select-appointment-channel"
                  labelId="label-appointment-channel"
                  value={appointmentChannelId}
                  options={[
                    <MenuItem key={0} value={0}>
                      {t(`REPORT.FILTER.ALL`)}
                    </MenuItem>,
                    ..._.map(appointmentChannels, (c) => (
                      <MenuItem key={c.appointmentChannelId} value={c.appointmentChannelId}>
                        {c.channelName}
                      </MenuItem>
                    ))
                  ]}
                  formControlStyle={{ maxWidth: 'unset !important' }}
                  classesOption="style-select-doctors"
                />
              </Col>
              {/* TDH customization — status filter */}
              <Col xs={12} sm={6} md={6} xl={2} className={'pt-2 px-1'}>
                <FilterSelect
                  onchange={(e) => {
                    setStatusAppointment(String(e.target.value || 'ALL'))
                    setPage(1)
                  }}
                  renderValue={() =>
                    `${t('REPORT.FILTER.APPOINTMENT_STATUS')}: ${
                      statusAppointment && statusAppointment !== 'ALL' ? t(`APPOINTMENT.STATUS.${statusAppointment}`) : t('REPORT.FILTER.ALL')
                    }`
                  }
                  label={''}
                  selectId="select-status-appointment"
                  labelId="label-status-appointment"
                  value={statusAppointment}
                  options={[
                    <MenuItem key="ALL" value="ALL">{t('REPORT.FILTER.ALL')}</MenuItem>,
                    ...['NORMAL', 'CONFIRM', 'PUT_OFF', 'CAN_NOT_CONTACT', 'NOT_YET', 'ARRIVED', 'PENDING_PAYMENT', 'PAYMENT', 'PENDING_SERVICE', 'SERVICE', 'DONE', 'CANCEL'].map((s) => (
                      <MenuItem key={s} value={s}>{t(`APPOINTMENT.STATUS.${s}`)}</MenuItem>
                    ))
                  ]}
                  formControlStyle={{ maxWidth: 'unset !important' }}
                  classesOption="style-select-doctors"
                />
              </Col>
              {/* TDH customization — patient filter (server-side search-as-you-type, single-select like register page) */}
              <Col xs={12} sm={6} md={6} xl={3} className={'pt-2 px-1'}>
                <SearchPatients
                  status="ACTIVE"
                  patientId={selectedPatientId}
                  callbackPatientId={(val: any) => {
                    setSelectedPatientId(Number(val) || 0)
                    setPage(1)
                  }}
                  callbackPatient={(p: any) => {
                    if (p?.firstname || p?.lastname) {
                      setSelectedPatientName(`${p?.prefixName || ''}${p?.firstname || ''} ${p?.lastname || ''}`.trim())
                    } else {
                      setSelectedPatientName('')
                    }
                  }}
                  onClear={() => {
                    setSelectedPatientId(0)
                    setSelectedPatientName('')
                    setPage(1)
                  }}
                  bodyHeight={32}
                  bodyWidth={'100%'}
                  optionBelowSideBar
                />
              </Col>
              {/* TDH customization — treatment items filter */}
              <Col xs={12} sm={6} md={6} xl={3} className={'pt-2 px-1'}>
                <ExcelCodeFilterSelect
                  inputLabel={t('REPORT.FILTER.TYPE_OPERATION')}
                  placeholder={`${t('INPUT.SEARCH')} ${t('REPORT.FILTER.TYPE_OPERATION')}...`}
                  data={_.map(operativeTypes, (o) => ({
                    id: o.operativeTypeId,
                    value: o.operativeTypeName,
                    label: o.operativeTypeName
                  })) as any}
                  selectedRows={_.map(operativeTypes, (o) => ({
                    id: o.operativeTypeId,
                    value: o.operativeTypeName,
                    label: o.operativeTypeName
                  })).filter((r) => selectedOperatives.includes(r.id)) as any}
                  onSelectionChange={(rows: any) => setSelectedOperatives(rows.map((r: any) => r.id))}
                  maxTags={1}
                />
              </Col>
              <Col xs={6} sm={6} md={6} xl={2} className={'pt-0 d-flex justify-content-end'}>
                <ButtonCustom
                  onClick={() => {
                    setFetch(true)
                    setLoading(true)
                  }}
                  textButton={t('INPUT.SEARCH')}
                  style={{ height: '30px', marginTop: '5px' }}
                />
              </Col>

              <Col xs={6} sm={6} md={6} xl={4} className={'d-flex justify-content-end pt-2 px-1 ml-auto'}>
                <UseStyled.DropdownDocument title={t('REGISTER.BUTTON.PRINT_DOCUMENT')} id="dropdown-basic" className="ml-xl-auto">
                  <UseStyled.DropdownItem onClick={onDownload}>{t('Excel')}</UseStyled.DropdownItem>
                  {/* <FucntionPrint
                    content={() => componentRef.current}
                    removeIfram
                    trigger={() => <UseStyled.DropdownItem>{t('PDF')}</UseStyled.DropdownItem>}
                    style={`@page { size: 1122.519685px 793.7007874px;  margin: 0.5cm; } @media print { body { -webkit-print-color-adjust: exact; }`}
                  /> */}
                </UseStyled.DropdownDocument>
              </Col>
            </Row>
          </Card.Title>
          <Box className={'mt-3 pl-xl-3 pr-xl-2 pb-4'}>
            <AppointmentList
              filterTable={getAllFilter()}
              page={page}
              pageLimit={pageLimit}
              rowCount={rowCount}
              sortBy={sortBy}
              sortType={sortType}
              data={appointments}
              dataExport={exportAppointments}
              date={date}
              onRequestSort={onRequestSort}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              handleChangePage={handleChangePage}
              componentRef={componentRef}
            />
          </Box>
        </Card.Body>
        <Loading show={loading} type="fullLoading" />
      </Card>
    </>
  )
}
